// keywordStyles.ts

/**
 * Interface for keyword style configuration
 */
interface KeywordStyle {
  /** CSS class for the keyword itself */
  keywordClass: string
  /** CSS class for the content following the keyword */
  contentClass: string
}

/**
 * Interface for the complete keyword styles mapping
 */
interface KeywordStyles {
  [keyword: string]: KeywordStyle
}

/**
 * CSS classes syntax highlighting of keywords
 * Maps questionnaire language keywords to their corresponding CSS classes
 */
const keywordStyles: KeywordStyles = {
  DATA: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "INSERT AT": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "INSERT AT:": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "REDEFINE LIST": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "FOR EACH": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  TERMINATE: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "SKIP TO": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  IF: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "IF SELECTED": {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  SELECTED: {
    keywordClass: "highlight-if-selected",
    contentClass: "highlight-item",
  },
  BEGIN: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  END: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "FILL IN TEXT": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "FILL IN NUMBER": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-note",
  },
  "FILL IN REAL": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-note",
  },
  HIDE: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  HIDDEN: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  RECODE: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  VALIDATE: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-validate",
  },
  "SELECT FROM": {
    keywordClass: "highlight-bold",
    contentClass: "",
  },
  "SELECT ONE FROM": {
    keywordClass: "highlight-bold",
    contentClass: "",
  },
  "SELECT MULTIPLE FROM": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "SELECT MANY FROM": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  PER: {
    keywordClass: "highlight-bold",
    contentClass: "",
  },
  "SELECT FROM RANGE": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  QUESTION: {
    keywordClass: "highlight-bold",
    contentClass: "",
  },
  Q: {
    keywordClass: "highlight-bold",
    contentClass: "",
  },
  "RANGE LIST": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  EXTEND: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  CHANGE: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  REMOVE: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-note",
  },
  "ADD LIST": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  TRIM: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  HEADER: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  SECTION: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item ",
  },
  SURVEY: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "INSERT AFTER": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  LIST: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  EXCEPTIONS: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  "LIST EXCEPTIONS": {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  LOCATION: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  RANDOMIZE: {
    keywordClass: "highlight-bold",
    contentClass: "highlight-item",
  },
  DEFINESPLIT: {
    keywordClass: "highlight-normal",
    contentClass: "highlight-item",
  },
  AS: {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  "ANY OF": {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  OR: {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  AND: {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  IS: {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  "IS NOT": {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  COUNT: {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  PRIORITY: {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  RANDOM: {
    keywordClass: "highlight-as",
    contentClass: "",
  },
  // Add more keywords as necessary with corresponding styles
} as const

/**
 * Interface for the block-keyword → highlight-class mapping.
 */
interface BlockKeywordsMap {
  [keyword: string]: string
}

/**
 * Block keywords and their corresponding highlight classes. A block keyword
 * at the start of a line styles that line and its continuation lines until
 * the next keyword/blank line (see FontChangeExtension's block handling).
 */
const blockKeywords: BlockKeywordsMap = {
  "ADD LIST": "highlight-item",
  LIST: "highlight-item",
  EXCEPTIONS: "highlight-item",
  "LIST EXCEPTIONS": "highlight-item",
  NOTE: "highlight-note",
  TEXT: "highlight-text-black",
  HEADER: "highlight-item",
  ADD: "highlight-item",
  CHANGE: "highlight-item",
  "RANGE LIST": "highlight-item",
  PER: "highlight-item",
  "SELECT ONE FROM": "highlight-item",
  "SELECT MANY FROM": "highlight-item",
  "SELECT MULTIPLE FROM": "highlight-item",
  TRIM: "highlight-item",
  REMOVE: "highlight-item",
  EXTEND: "highlight-item",
  RECODE: "highlight-item",
}

/**
 * Special (mid-line) keywords — logical/operator words that are highlighted
 * wherever they appear on a line, outside of `<<…>>` variable references.
 */
const specialKeywords: string[] = [
  "IF",
  "IF SELECTED",
  "ANY OF",
  "OR",
  "AND",
  "IS",
  "IS NOT",
  "IN",
  "WHERE",
  "NOT",
  "COUNT",
  "PRIORITY",
  "RANDOM",
]

export default keywordStyles
export { blockKeywords, specialKeywords }
export type { KeywordStyle, KeywordStyles, BlockKeywordsMap }
